import { DeleteFilled, DeleteOutlined, WarningFilled } from "@ant-design/icons";
import {
  Button,
  Form,
  message,
  notification,
  Popconfirm,
  Space,
  Typography,
} from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

import {
  useDeleteAutocategorizationRuleMutation,
  useUpdateAutocategorizationRuleMutation,
} from "../../../api/graphql/mutations/autocategorizationRule";
import { useAutocategorizationRuleQuery } from "../../../api/graphql/queries/autocategorizationRule";
import { IMessageType } from "../../../types";
import { showMessage } from "../../../utils";
import { getServerErrorNotification } from "../../../utils/error";
import ActionLogDrawerAsync from "../../common/ActionLogDrawerAsync";
import { ContentWrapper } from "../../common/styledComponents";
import RuleForm from "./RuleForm";
import { RuleStatusSwitch } from "./RuleStatusSwitch";
import { FormHeading } from "./styledComponents";

export const EditRule = () => {
  const { t } = useTranslation(["Rules", "translation"]);
  const { ruleId } = useParams();
  const navigate = useNavigate();

  const id = parseInt(ruleId || "0");

  const autocategorizationRuleResult = useAutocategorizationRuleQuery({
    variables: { id },
    onCompleted: (data) => {
      if (data.autocategorizationRule === null) {
        showMessage({
          type: IMessageType.ERROR,
          duration: 3,
          content: t("page.notFound", { id }),
        });
        navigate("/rules-view");
      }
    },
    onError: (error) => {
      notification.error(
        getServerErrorNotification(error, t("page.fetchError", { id }))
      );
      navigate("/rules-view");
    },
  });

  const rule = autocategorizationRuleResult.data?.autocategorizationRule;

  const [updateRule, updateRuleResult] =
    useUpdateAutocategorizationRuleMutation({
      onCompleted: () => {
        showMessage({
          type: IMessageType.SUCCESS,
          duration: 3,
          content: t("page.saved"),
        });
        window.opener?.location.reload();
        window.close();
      },
      onError: (error) => {
        notification.error(
          getServerErrorNotification(
            error,
            t("page.updateError", { id: rule?.id })
          )
        );
      },
    });

  const [deleteRule, deleteRuleResult] =
    useDeleteAutocategorizationRuleMutation({
      onCompleted: () => {
        message.info({
          duration: 3,
          content: t("page.deleted", { id: rule?.id }),
          icon: <DeleteFilled />,
        });
        window.opener?.location.reload();
        window.close();
      },
      onError: (error) => {
        notification.error(
          getServerErrorNotification(
            error,
            t("page.deleteError", { id: rule?.id })
          )
        );
      },
    });

  return (
    <ContentWrapper padding="0">
      <RuleForm
        rule={rule ?? undefined}
        title={
          <Space align="center">
            <FormHeading>{t("page.editTitle", { id: rule?.id })}</FormHeading>
            <Typography.Text type="secondary">
              {t("page.editSubtitle")}
            </Typography.Text>
          </Space>
        }
        loading={
          autocategorizationRuleResult.loading ||
          updateRuleResult.loading ||
          deleteRuleResult.loading
        }
        onSubmit={(updatedRule) => {
          if (rule?.id) {
            updateRule({
              variables: {
                input: {
                  id: rule.id,
                  ...updatedRule,
                },
              },
            });
          }
        }}
      >
        <RuleStatusSwitch
          name="enabled"
          appliedTimesCount={rule?.appliedTimesCount || undefined}
          initialEnabled={rule?.enabled}
        />

        <Form.Item>
          <ActionLogDrawerAsync
            title={t("page.actionLog")}
            modelName="auto_categorization_rule"
            recordIds={[`${id}`]}
            renderButton={({ onClick }) => (
              <Button onClick={onClick}>{t("page.viewLogs")}</Button>
            )}
          />
        </Form.Item>

        <Form.Item label={t("page.delete.label")} required>
          <Popconfirm
            title={t("page.delete.confirm")}
            cancelText={t("translation:no")}
            okText={t("page.delete.ok")}
            icon={<WarningFilled style={{ color: "red" }} />}
            onConfirm={() => {
              if (rule?.id) {
                deleteRule({
                  variables: {
                    id: rule.id,
                  },
                });
              }
            }}
          >
            <Button icon={<DeleteOutlined />} danger>
              {t("page.delete.button")}
            </Button>
          </Popconfirm>
        </Form.Item>
      </RuleForm>
    </ContentWrapper>
  );
};
